import { useCallback, useContext, useEffect, useState } from "react";
import AccountsApi from "../../../api/AccountsApi";
import { useRecoilState } from "recoil";
import { memberState } from "../../../state/atoms";
import { handleServerError } from "../../../util/ErrorHandler";
import { Empty, Table, Image, Button, Skeleton } from "antd";
import { ColumnType } from "antd/lib/table/interface";
import DateTimeStamp from "../misc/DateTimeStamp";
import {
  getPrintInfoPath,
  getPrintItemCount,
  getPrintPreviewPath,
  getUserFriendlyPrintStatus,
} from "../../../service/BillingService";
import Price from "../misc/Price";
import { getFriendlyOrderStatus, openInNewTab } from "../../../util/utils";
import "./PrintHistoryPage.scss";
import { fallbackImage } from "../../../util/constants";
import OrdersApi from "../../../api/OrdersApi";
import { env } from "../../../env/env";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../AppContext";

const locale = {
  emptyText: (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      imageStyle={{ height: 60 }}
      description={<span> No data yet to show</span>}
    />
  ),
};

export function PrintHistoryPageNew() {
  const navigate = useNavigate();
  const { location } = useContext(AppContext);
  const [member] = useRecoilState(memberState);
  const [prints, setPrints] = useState([] as any[]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([] as ColumnType<any>[]);
  const [data, setData] = useState([] as any[]);

  useEffect(() => {
    if (!member.id) return;

    setLoading(true);

    AccountsApi.getPrintHistory(member.id)
      .then((resp) => {
        let printsOld = resp.data as any[]
        // let printsOld = [[] as any[]]
        printsOld.forEach(p => { p.isNew = false })

        OrdersApi.getLocationOrders(location.internalId!)
          .then((resp2) => {
            let printsNew = [] as any[]

            resp2.data.forEach(p => {
              const firstItem = p.items![0]

              let thumbPath = `${env.rendererPath}/design/thumbnail/${firstItem.designId}?height=150` 
              
              if (firstItem.thumbs) {
                thumbPath = env.s3Path + '/' + JSON.parse(firstItem.thumbs)[0].path
              }

              printsNew.push({
                id: p.number,
                orderId: p.id,
                menuId: (p.items && p.items![0]) ? p.items![0].designId : '',
                finishedDate: p.purchaseDate,
                total: p.total,
                items: p.items!,
                location: location.name,
                status: getFriendlyOrderStatus(p.status!),
                isNew: true,
                thumbPath: thumbPath
              })
            })

            printsNew = printsNew.concat(printsOld)
            printsNew.sort(
              (a, b) => parseInt(b.finishedDate!) - parseInt(a.finishedDate!)
            )

            setPrints(printsNew);
            setLoading(false);
          })
          .catch((e) => {
            handleServerError(e);
            setLoading(false);
          });
    })
    .catch((e) => {
      handleServerError(e);
      setLoading(false);
    });
  }, [member.id, member.internalId, location.internalId, location.name]);

  function getTitle(key: string): string {
    switch (key) {
      case "menuId":
      case "designId":
        return "Preview";
      case "finishedDate":
      case "purchaseDate":
        return "Date";
      case "id":
        return "Order #";
      case "total":
        return "Total";
      case "items":
        return "Items";
      case "location":
        return "Location";
      case "status":
        return "Status";
      default:
        return "";
    }
  }

  const viewOrderDetails = useCallback((print: any) => {
    if (print.isNew) {
      navigate(`order/${print.orderId}`);
    } else {
      openInNewTab(getPrintInfoPath(print.id));
    }
  }, [navigate])

  const getColumnContent = useCallback(
    (text: string, print: any, key: string) => {
      switch (key) {
        case "menuId":
          return (
            <Image
              width={"auto"}
              height={"auto"}
              src={ print.isNew
                      ? `${print.thumbPath}` 
                      : getPrintPreviewPath(print) }
              fallback={fallbackImage}
              preview={false}
            />
          );
        case "finishedDate":
          return <DateTimeStamp date={text} dateOnly />;
        case "total":
          return (
            <span>
              <Price amount={text} />
            </span>
          );
        case "status":
          return (
            <div className={"col-status"}>
              <span>{!print.isNew ? getUserFriendlyPrintStatus(parseInt(text)) : text}</span>
              <div className={"actions"}>
                <Button
                  className={"secondary"}
                  onClick={() => viewOrderDetails(print)}
                >
                  Order Details
                </Button>
              </div>
            </div>
          );
        case "items":
          return <span>{print.isNew ? print.items.length : getPrintItemCount(print)}</span>;
        default:
          return <span>{text}</span>;
      }
    },
    [viewOrderDetails]
  );

  useEffect(() => {
    if (!prints || !prints.length) return;

    let isMobile = false;
    if (window.innerWidth <= 500) {
      isMobile = true;
    }

    const tableColumns = [] as string[];
    tableColumns.push("menuId");
    if (!isMobile) tableColumns.push("finishedDate");
    if (!isMobile) tableColumns.push("id");
    if (!isMobile) tableColumns.push("total");
    if (!isMobile) tableColumns.push("items");
    if (!isMobile) tableColumns.push("location");
    tableColumns.push("status");

    setColumns(
      tableColumns.map((key) => {
        return {
          title: getTitle(key),
          dataIndex: key,
          key,
          render: (text: string, print) =>
            getColumnContent(text, print, key),
        };
      })
    );

    setData(
      prints.map((it) => {
        return {
          key: it.id,
          id: it.id,
          orderId: it.isNew ? it.orderId : '',
          total: it.total,
          finishedDate: it.finishedDate,
          items: it.items,
          location: it.location,
          status: it.status,
          menuId: it.menuId,
          test: it.test,
          isNew: it.isNew,
          thumbPath: it.thumbPath,
        };
      })
    );
  }, [prints, getColumnContent]);

  return (
    <div className={"print-history-page content"}>
      {loading && <Skeleton />}

      {!loading && (
        <>
          <h3>Print History</h3>

          <Table
            rowClassName={(record) => (!record.location ? "cb" : "")}
            locale={locale}
            className={"table"}
            loading={loading}
            columns={columns}
            dataSource={data}
            pagination={false}
          />
        </>
      )}
    </div>
  );
}
